/**
 * Onderdeel Document Formulier - Nieuw/Bewerken
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiFetch, { getApiBaseUrl } from '../../services/api';
import ErrorDialog from '../../components/ErrorDialog';
import Toast from '../../components/Toast';
import './Onderdelen.css';

interface VoorraadItem {
  id: number;
  onderdeel_type: string;
  fabrikant: string | null;
  model: string | null;
  leverancier: string | null;
  artikelnummer: string | null;
}

interface DocumentFormData {
  voorraad_item_id: number;
  document_type: string;
  titel: string;
  bestand_pad: string;
  taal: string;
  versie: string;
  omschrijving: string;
  actief: boolean;
}

const DOCUMENT_TYPES = [
  'Handleiding',
  'Informatie',
  'Specificaties',
  'Installatiehandleiding',
  'Onderdelenlijst',
  'Garantievoorwaarden',
  'CE-certificaat',
  'Technische tekening',
  'Overig'
];

const OnderdeelDocumentForm = () => {
  const navigate = useNavigate();
  const { onderdeelId, documentId } = useParams<{ onderdeelId: string; documentId?: string }>();
  const isEditMode = !!documentId;

  const [onderdeel, setOnderdeel] = useState<VoorraadItem | null>(null);
  const [formData, setFormData] = useState<DocumentFormData>({
    voorraad_item_id: parseInt(onderdeelId || '0'),
    document_type: 'Handleiding',
    titel: '',
    bestand_pad: '',
    taal: 'NL',
    versie: '',
    omschrijving: '',
    actief: true,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadOnderdeel = useCallback(async () => {
    if (!onderdeelId) return;

    try {
      const data = await apiFetch(`/api/v1/voorraad/items/${onderdeelId}`);
      setOnderdeel(data);
    } catch (err: any) {
      setError(err.message);
    }
  }, [onderdeelId]);

  const loadDocument = useCallback(async () => {
    if (!documentId) return;

    try {
      setLoading(true);
      const document = await apiFetch(`/api/v1/onderdeel-documenten/${documentId}`);
      setFormData({
        voorraad_item_id: document.voorraad_item_id,
        document_type: document.document_type,
        titel: document.titel || '',
        bestand_pad: document.bestand_pad,
        taal: document.taal || 'NL',
        versie: document.versie || '',
        omschrijving: document.omschrijving || '',
        actief: document.actief,
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    loadOnderdeel();
    if (isEditMode) {
      loadDocument();
    }
  }, [isEditMode, loadOnderdeel, loadDocument]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (isEditMode && documentId) {
        await apiFetch(`/api/v1/onderdeel-documenten/${documentId}`, {
          method: 'PUT',
          body: JSON.stringify(formData)
        });
        setSuccess('Document succesvol bijgewerkt!');
      } else {
        await apiFetch('/api/v1/onderdeel-documenten', {
          method: 'POST',
          body: JSON.stringify(formData)
        });
        setSuccess('Document succesvol aangemaakt!');
      }

      setTimeout(() => {
        navigate(`/onderdelen/${onderdeelId}/documenten`);
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return <div className="loading">Laden...</div>;
  }

  if (!onderdeel) {
    return <div className="loading">Onderdeel laden...</div>;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>{isEditMode ? 'Document Bewerken' : 'Nieuw Document'} - {onderdeel.onderdeel_type}</h1>
      </div>

      <Toast message={success} type="success" onClose={() => setSuccess(null)} />

      <form onSubmit={handleSubmit} className="onderdeel-form">
        <div className="form-section">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="document_type">Type *</label>
              <select
                id="document_type"
                name="document_type"
                value={formData.document_type}
                onChange={handleChange}
                required
              >
                {DOCUMENT_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="titel">Titel</label>
              <input
                type="text"
                id="titel"
                name="titel"
                value={formData.titel}
                onChange={handleChange}
                placeholder="bijv. Installatiehandleiding v2.0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="taal">Taal</label>
              <input
                type="text"
                id="taal"
                name="taal"
                value={formData.taal}
                onChange={handleChange}
                placeholder="NL"
                maxLength={10}
              />
            </div>

            <div className="form-group">
              <label htmlFor="versie">Versie</label>
              <input
                type="text"
                id="versie"
                name="versie"
                value={formData.versie}
                onChange={handleChange}
                placeholder="v2.0"
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="bestand_pad">Bestand Pad *</label>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <input
                  type="file"
                  accept=".pdf"
                  style={{ display: 'none' }}
                  id="file-input"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const fileName = file.name;

                      try {
                        const token = localStorage.getItem('access_token');
                        const formDataUpload = new FormData();
                        formDataUpload.append('filename', fileName);

                        const response = await fetch(`${getApiBaseUrl()}/api/v1/files/find-pdf`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${token}`
                          },
                          body: formDataUpload
                        });

                        if (response.ok) {
                          const result = await response.json();
                          setFormData(prev => ({
                            ...prev,
                            bestand_pad: result.path
                          }));
                          setSuccess('Bestand gevonden en pad ingevuld!');
                          setError(null);
                        } else {
                          const errorResult = await response.json();
                          setError(`Fout: ${errorResult.detail}`);
                        }
                      } catch (err: any) {
                        setError(`Fout bij zoeken van bestand: ${err.message}`);
                      }
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => document.getElementById('file-input')?.click()}
                >
                  Kies PDF Bestand
                </button>
              </div>
              <input
                type="text"
                id="bestand_pad"
                name="bestand_pad"
                value={formData.bestand_pad}
                onChange={handleChange}
                required
                placeholder="documents/onderdelen/bestandsnaam.pdf"
              />
              <small style={{ display: 'block', marginTop: '5px', color: '#64748b' }}>
                Klik "Kies PDF Bestand" om een bestand te selecteren.
              </small>
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="omschrijving">Omschrijving</label>
              <textarea
                id="omschrijving"
                name="omschrijving"
                value={formData.omschrijving}
                onChange={handleChange}
                rows={3}
                placeholder="Extra informatie over dit document..."
              />
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="actief"
                  checked={formData.actief}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, actief: e.target.checked }))
                  }
                />
                Actief
              </label>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate(`/onderdelen/${onderdeelId}/documenten`)}
          >
            Annuleren
          </button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Bezig...' : isEditMode ? 'Opslaan' : 'Aanmaken'}
          </button>
        </div>
      </form>

      <ErrorDialog
        isOpen={!!error}
        message={error || ''}
        onClose={() => setError(null)}
      />
    </div>
  );
};

export default OnderdeelDocumentForm;